"use client";

import Link from "next/link";
import { useParams, useSearchParams } from "next/navigation";
import { useQueryStates } from "nuqs";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useSyncExternalStore,
} from "react";
import {
  AiChatUiContext,
  type AiChatUiContextType,
  type ChatLinkComponentProps,
  type ChatOrderDirection,
  type ChatOrderKey,
} from "@/lib/ai/ai-chat-ui-context";
import {
  readStoredChatModel,
  useStoredAutoApproveToolConfirmations,
  writeStoredAutoApproveToolConfirmations,
  writeStoredChatModel,
} from "@/lib/ai/chat-preferences";
import {
  isRestrictedHighTierModel,
  resolveAiModelAccess,
} from "@/lib/ai/model-access";
import {
  DEFAULT_MODEL_ID,
  type ModelId,
  normalizeModelId,
} from "@/lib/ai/models";
import { useAnalysisTargetSelectionState } from "@/lib/ai/use-analysis-target-selection-state";
import {
  readStoredVisualizationEnabled,
  VisualizationUiProvider,
  writeStoredVisualizationEnabled,
} from "@/lib/ai/visualization/client/ui-context";
import { useRouter } from "@/lib/navigation";
import { cn } from "@/lib/utils";
import {
  AI_CHAT_ORDER_BY_FALLBACK,
  AI_CHAT_VISUALIZATION_OFF_VALUE,
  AI_CHAT_VISUALIZATION_QUERY_KEY,
  aiChatPath,
  aiChatRootPath,
  aiChatSearchParamParsers,
  serializeAiChatSearchParams,
} from "./_search-params/ai-chat-search-params";

type Props = {
  children: ReactNode;
  workspaceId: string;
  chatId?: string | null;
  contractOptions?: readonly string[];
  restrictedModelIds: readonly ModelId[];
};

const locationHashSubscribers = new Set<() => void>();
const notifyLocationHashSubscribers = () => {
  for (const subscriber of locationHashSubscribers) subscriber();
};

function subscribeToLocationHash(subscriber: () => void): () => void {
  if (locationHashSubscribers.size === 0) {
    window.addEventListener("hashchange", notifyLocationHashSubscribers);
    window.addEventListener("popstate", notifyLocationHashSubscribers);
  }
  locationHashSubscribers.add(subscriber);

  return () => {
    locationHashSubscribers.delete(subscriber);
    if (locationHashSubscribers.size === 0) {
      window.removeEventListener("hashchange", notifyLocationHashSubscribers);
      window.removeEventListener("popstate", notifyLocationHashSubscribers);
    }
  };
}

function getCurrentLocationHash(): string {
  return window.location.hash;
}

/** chat link 間で購読を共有し、現在 hash を pathname 遷移後も保持する。 */
function useCurrentLocationHash(): string {
  return useSyncExternalStore(
    subscribeToLocationHash,
    getCurrentLocationHash,
    () => "",
  );
}

const ChatLinkComponent = ({
  chatId,
  isSelected,
  children,
  className,
}: ChatLinkComponentProps) => {
  const params = useParams();
  const searchParams = useSearchParams();
  const currentHash = useCurrentLocationHash();
  const langParam = typeof params?.lang === "string" ? params.lang : "";
  const workspaceFromParams =
    typeof params?.workspaceId === "string"
      ? decodeURIComponent(params.workspaceId)
      : "";
  const href = serializeAiChatSearchParams(
    aiChatPath(langParam, workspaceFromParams, chatId),
    searchParams,
    currentHash,
    {},
  );

  return (
    <Link
      href={href}
      prefetch={false}
      scroll={false}
      aria-current={isSelected ? "page" : undefined}
      className={cn("relative flex items-center w-full", className)}
    >
      {children}
    </Link>
  );
};

/**
 * AI チャットページ用 Provider。URL パラメータ（chatId, orderBy, includesShared, model, viz）を
 * コンテキスト経由で UI に供給する。
 *
 * @remarks
 * 可視化トグルは plugin 固有 state なので、共通 `AiChatUiContext` には載せず
 * `VisualizationUiProvider` 側へ分離して共有する。
 */
export function AiChatPageProvider({
  children,
  workspaceId,
  chatId,
  contractOptions = [],
  restrictedModelIds,
}: Props) {
  const params = useParams();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [preferenceQuery, setPreferenceQuery] = useQueryStates(
    aiChatSearchParamParsers,
  );

  const langParam = typeof params?.lang === "string" ? params.lang : "";
  const workspaceFromParams =
    typeof params?.workspaceId === "string"
      ? decodeURIComponent(params.workspaceId)
      : "";
  const resolvedWorkspaceId = workspaceFromParams || workspaceId;
  const analysisScopeStorageKey = `ai-chat-page-analysis-scope:${resolvedWorkspaceId}`;
  const {
    analysisTargets,
    pageAnalysisTarget,
    manualAnalysisTargets,
    setAnalysisTargets,
    addAnalysisTarget,
    removeAnalysisTarget,
    removeLastAnalysisTarget,
    clearAnalysisTargets,
    setPageAnalysisTarget,
  } = useAnalysisTargetSelectionState({
    storageKey: analysisScopeStorageKey,
  });

  const selectedChatId =
    typeof params.chatId === "string" && params.chatId.length > 0
      ? decodeURIComponent(params.chatId)
      : null;

  const rawOrderBy = preferenceQuery.orderBy;
  const orderKey: ChatOrderKey = rawOrderBy.includes("CREATED_AT")
    ? "CREATED_AT"
    : "UPDATED_AT";
  const orderDirection: ChatOrderDirection = rawOrderBy.endsWith("_ASC")
    ? "ASC"
    : "DESC";
  const includesShared = preferenceQuery.includesShared !== "false";
  const visualizationQuery = preferenceQuery.viz;
  const hasVisualizationQuery =
    visualizationQuery === AI_CHAT_VISUALIZATION_OFF_VALUE ||
    searchParams.has(AI_CHAT_VISUALIZATION_QUERY_KEY);
  const isVisualizationEnabledFromQuery =
    visualizationQuery !== AI_CHAT_VISUALIZATION_OFF_VALUE;

  const queryModel = preferenceQuery.model;
  const validQueryModel = normalizeModelId(queryModel);
  const aiModelAccess = useMemo(
    () => resolveAiModelAccess(contractOptions, true, restrictedModelIds),
    [contractOptions, restrictedModelIds],
  );
  const resolveSelectableModel = useCallback(
    (modelId: ModelId | null) => {
      if (!modelId) return null;
      if (isRestrictedHighTierModel(modelId, aiModelAccess)) {
        return DEFAULT_MODEL_ID;
      }
      return modelId;
    },
    [aiModelAccess],
  );
  const safeQueryModel = resolveSelectableModel(validQueryModel);
  const currentModel = safeQueryModel ?? DEFAULT_MODEL_ID;
  const isVisualizationEnabled = hasVisualizationQuery
    ? isVisualizationEnabledFromQuery
    : true;
  const serializeCurrentAiChatSearchParams = useCallback(
    (pathname: string) =>
      serializeAiChatSearchParams(
        pathname,
        searchParams,
        typeof window === "undefined" ? "" : window.location.hash,
        {
          model: queryModel,
          viz:
            visualizationQuery === AI_CHAT_VISUALIZATION_OFF_VALUE
              ? AI_CHAT_VISUALIZATION_OFF_VALUE
              : null,
          orderBy:
            preferenceQuery.orderBy !== AI_CHAT_ORDER_BY_FALLBACK ||
            searchParams.has("orderBy")
              ? preferenceQuery.orderBy
              : null,
          includesShared:
            preferenceQuery.includesShared === "false" ? "false" : null,
        },
      ),
    [preferenceQuery, queryModel, searchParams, visualizationQuery],
  );

  useEffect(() => {
    const nextQuery: {
      model?: string | null;
      viz?: typeof AI_CHAT_VISUALIZATION_OFF_VALUE | null;
    } = {};

    /**
     * AI チャットページでは URL query を表示 state の正本にし、
     * storage は hydration 後に query へ補完する。
     */
    const storedModel = resolveSelectableModel(readStoredChatModel());

    if (safeQueryModel) {
      writeStoredChatModel(safeQueryModel);
      if (queryModel !== safeQueryModel) {
        nextQuery.model = safeQueryModel;
      }
    } else if (storedModel) {
      nextQuery.model = storedModel;
    }

    const storedVisualizationEnabled = readStoredVisualizationEnabled();
    if (hasVisualizationQuery) {
      writeStoredVisualizationEnabled(isVisualizationEnabledFromQuery);
    } else {
      if (storedVisualizationEnabled === false) {
        nextQuery.viz = AI_CHAT_VISUALIZATION_OFF_VALUE;
      }
    }

    // model と viz の両方が未指定な初回表示でも、URL 正規化を 1 回にまとめて
    // query の競合上書きを避ける。
    if (Object.keys(nextQuery).length === 0) {
      return;
    }

    void setPreferenceQuery(nextQuery);
  }, [
    safeQueryModel,
    queryModel,
    setPreferenceQuery,
    resolveSelectableModel,
    hasVisualizationQuery,
    isVisualizationEnabledFromQuery,
  ]);

  const selectModel = useCallback(
    (modelId: ModelId) => {
      const safeModel = resolveSelectableModel(modelId) ?? DEFAULT_MODEL_ID;
      writeStoredChatModel(safeModel);
      void setPreferenceQuery({ model: safeModel });
    },
    [resolveSelectableModel, setPreferenceQuery],
  );

  const selectChat = useCallback(
    (nextChatId: string) => {
      router.replace(
        serializeCurrentAiChatSearchParams(
          aiChatPath(langParam, resolvedWorkspaceId, nextChatId),
        ),
      );
    },
    [
      langParam,
      resolvedWorkspaceId,
      router,
      serializeCurrentAiChatSearchParams,
    ],
  );

  const setOrder: AiChatUiContextType["setOrder"] = useCallback(
    (key, direction) => {
      void setPreferenceQuery({ orderBy: `${key}_${direction}` });
    },
    [setPreferenceQuery],
  );

  const setIncludesShared = useCallback(
    (include: boolean) => {
      void setPreferenceQuery({
        includesShared: include ? null : "false",
      });
    },
    [setPreferenceQuery],
  );

  const setVisualizationEnabled = useCallback(
    (enabled: boolean) => {
      writeStoredVisualizationEnabled(enabled);
      void setPreferenceQuery({
        viz: enabled ? null : AI_CHAT_VISUALIZATION_OFF_VALUE,
      });
    },
    [setPreferenceQuery],
  );

  const clearChatSelection = useCallback(() => {
    router.replace(
      serializeCurrentAiChatSearchParams(
        aiChatRootPath(langParam, resolvedWorkspaceId),
      ),
    );
  }, [
    langParam,
    resolvedWorkspaceId,
    router,
    serializeCurrentAiChatSearchParams,
  ]);

  const autoApproveToolConfirmations = useStoredAutoApproveToolConfirmations();

  const setAutoApproveToolConfirmations = useCallback((enabled: boolean) => {
    writeStoredAutoApproveToolConfirmations(enabled);
  }, []);

  const value: AiChatUiContextType = useMemo(
    () => ({
      chatId: chatId ?? selectedChatId ?? null,
      workspaceId: resolvedWorkspaceId,
      lang: langParam || null,
      chatLinkComponent: ChatLinkComponent,
      selectChat,
      clearChatSelection,
      currentModel,
      selectModel,
      aiModelAccess,
      analysisTargets,
      pageAnalysisTarget,
      manualAnalysisTargets,
      setAnalysisTargets,
      addAnalysisTarget,
      removeAnalysisTarget,
      removeLastAnalysisTarget,
      clearAnalysisTargets,
      setPageAnalysisTarget,
      orderKey,
      orderDirection,
      includesShared,
      setOrder,
      toggleOrderDirection: () =>
        setOrder(orderKey, orderDirection === "ASC" ? "DESC" : "ASC"),
      setIncludesShared,
      autoApproveToolConfirmations,
      setAutoApproveToolConfirmations,
    }),
    [
      chatId,
      selectedChatId,
      resolvedWorkspaceId,
      langParam,
      selectChat,
      clearChatSelection,
      currentModel,
      selectModel,
      aiModelAccess,
      analysisTargets,
      pageAnalysisTarget,
      manualAnalysisTargets,
      setAnalysisTargets,
      addAnalysisTarget,
      removeAnalysisTarget,
      removeLastAnalysisTarget,
      clearAnalysisTargets,
      setPageAnalysisTarget,
      orderKey,
      orderDirection,
      includesShared,
      setOrder,
      setIncludesShared,
      autoApproveToolConfirmations,
      setAutoApproveToolConfirmations,
    ],
  );

  return (
    <AiChatUiContext.Provider value={value}>
      <VisualizationUiProvider
        isVisualizationEnabled={isVisualizationEnabled}
        setVisualizationEnabled={setVisualizationEnabled}
      >
        {children}
      </VisualizationUiProvider>
    </AiChatUiContext.Provider>
  );
}
